import {
  OccupiedOpeningSpanType,
  OpeningSpanSourceType
} from 'utils/opening-free-slot-types';
import { clampOpeningOffsetOnRun } from 'utils/opening-position';
import { OpeningRunBoundsType } from 'utils/opening-position-types';
import { numericOpeningPosition } from 'utils/opening-position-value';

const OVERLAP_EPS_FT = 1e-6;

export function occupiedOpeningSpans(
  openings: OpeningSpanSourceType[]
): OccupiedOpeningSpanType[] {
  return openings.flatMap((opening) => {
    const offsetFt = numericOpeningPosition(opening.positionOnWall);
    return offsetFt === undefined ? [] : [{ offsetFt, widthFt: opening.width }];
  });
}

function overlapFt(
  offsetFt: number,
  widthFt: number,
  span: OccupiedOpeningSpanType
): number {
  const overlap =
    Math.min(offsetFt + widthFt, span.offsetFt + span.widthFt) -
    Math.max(offsetFt, span.offsetFt);
  return overlap > OVERLAP_EPS_FT ? overlap : 0;
}

function totalOverlapFt(
  offsetFt: number,
  widthFt: number,
  occupied: OccupiedOpeningSpanType[]
): number {
  return occupied.reduce(
    (sum, span) => sum + overlapFt(offsetFt, widthFt, span),
    0
  );
}

export function freeOpeningOffsetOnRun(
  desiredOffsetFt: number,
  widthFt: number,
  bounds: OpeningRunBoundsType,
  occupied: OccupiedOpeningSpanType[]
): number {
  const clamp = (offsetFt: number) =>
    clampOpeningOffsetOnRun(offsetFt, widthFt, bounds);
  const candidates = new Set<number>([
    clamp(desiredOffsetFt),
    ...occupied.flatMap((span) => [
      clamp(span.offsetFt + span.widthFt),
      clamp(span.offsetFt - widthFt)
    ])
  ]);

  const ranked = Array.from(candidates)
    .map((offsetFt) => ({
      offsetFt,
      overlapFt: totalOverlapFt(offsetFt, widthFt, occupied),
      distanceFt: Math.abs(offsetFt - desiredOffsetFt)
    }))
    .sort((a, b) => a.overlapFt - b.overlapFt || a.distanceFt - b.distanceFt);

  return ranked[0].offsetFt;
}
